import clsx from 'clsx'
import { computed, defineComponent } from 'vue'

import VButton from '@/components/common/button/VButton'
import VForm from '@/components/common/VForm'
import VStepper from '@/components/common/VStepper'
import useAuth from '@/composables/auth/useAuth'
import useBreakpoints from '@/composables/common/useBreakpoints'
import useLocalization from '@/composables/localization/useLocalization'
import useBookingView from '@/composables/views/booking/useBookingView'
import { getDisplayPatientVip } from '@/utils/booking'
import { dateTextFormat } from '@/utils/date'
import { getLocalizationName } from '@/utils/localization'

export default defineComponent(() => {
  const auth = useAuth()
  const { localeTexts } = useLocalization()
  const { isMobile } = useBreakpoints()
  const {
    bookingCompletedStep,
    serviceSelected,
    dateSelected,
    timeSelected,
    symptoms,
    doctorSelected,
    isRequireRoomForAdmission,
    admissionRoomSelected,
    admissionRoomStartDate,
    admissionRoomEndDate,
    firstName,
    lastName,
    email,
    phone,
    insurance,
    policyNumber,
    isPatientVip,
    bookingSteps,
    createBookingMutation,
    setBookingState
  } = useBookingView()

  // #region Getters

  const serviceName = computed((): string => {
    const name = getLocalizationName(serviceSelected.value!, auth.userProfile!.language_preference)

    return name ?? '-'
  })

  const mappedDoctor = computed((): string | null => {
    if (doctorSelected.value.id === '') {
      return '-'
    }

    return doctorSelected.value.name_th
  })

  const mappedDateTime = computed((): string => {
    return `${dateSelected.value && dateTextFormat(dateSelected.value)}, ${timeSelected.value?.time_range}`
  })

  const mappedSymptoms = computed((): string => {
    if (symptoms.value.trim() === '') {
      return '-'
    }

    return symptoms.value
  })

  const mappedRoomType = computed((): string => {
    if (!isRequireRoomForAdmission.value || admissionRoomSelected.value === null) {
      return '-'
    }

    const name = getLocalizationName(admissionRoomSelected.value, auth.userProfile!.language_preference)

    return name ?? '-'
  })

  const mappedStayDate = computed((): string => {
    if (!isRequireRoomForAdmission.value) {
      return '-'
    }

    return `${dateTextFormat(admissionRoomStartDate.value!)} - ${dateTextFormat(admissionRoomEndDate.value!)}`
  })

  const mappedEmail = computed((): string => {
    if (email.value === '') {
      return '-'
    }

    return email.value
  })

  const mappedInsurance = computed((): string => {
    if (insurance.value === '') {
      return '-'
    }

    return insurance.value
  })

  const mappedPolicyNumber = computed((): string => {
    if (policyNumber.value === '') {
      return '-'
    }

    return policyNumber.value
  })

  const patientVip = computed((): string => {
    return getDisplayPatientVip(isPatientVip.value)
  })

  // #endregion

  return () => (
    <VForm
      t-data='booking-confirmation'
      class='w-full'
      onSubmit={() => { createBookingMutation.execute() }}
    >
      <div class='mt-6'>
        <h3 class='text-heading-5'>{localeTexts.value['create-booking-confirm-step3-heading']}</h3>
        <div class='mt-2 text-body-1'>{localeTexts.value['create-booking-confirm-step3-paragraph']}</div>
        <div class='mt-6 divide-y divide-neutral-300 rounded border border-neutral-300'>
          <div class='p-4'>
            <div class='text-subtitle-1 text-neutral-500'>{localeTexts.value['create-booking-confirm-step3-visit-details-title']}</div>
            <div class='mt-4 space-y-2'>
              <div class='flex space-x-2'>
                <div class='text-body-1 font-medium'>{localeTexts.value['create-booking-confirm-step3-service-label']}</div>
                <div
                  t-data='booking-confirmation-service'
                  class='text-body-1'
                >{serviceName.value}
                </div>
              </div>
              <div class='flex space-x-2'>
                <div class='text-body-1 font-medium'>{localeTexts.value['create-booking-confirm-step3-visit-date-time-label']}</div>
                <div
                  t-data='booking-confirmation-datetime'
                  class='text-body-1'
                >{mappedDateTime.value}
                </div>
              </div>
              <div class='flex space-x-2'>
                <div class='text-body-1 font-medium'>{localeTexts.value['create-booking-confirm-step3-symptoms-label']}</div>
                <div
                  t-data='booking-confirmation-symptoms'
                  class='text-body-1'
                >{mappedSymptoms.value}
                </div>
              </div>
              <div class='flex space-x-2'>
                <div class='text-body-1 font-medium'>{localeTexts.value['create-booking-confirm-step3-doctor-label']}</div>
                <div
                  t-data='booking-confirmation-doctor'
                  class='text-body-1'
                >{mappedDoctor.value}
                </div>
              </div>
              <div class='flex space-x-2'>
                <div class='text-body-1 font-medium'>{localeTexts.value['create-booking-confirm-step3-room-label']}</div>
                <div
                  t-data='booking-confirmation-room'
                  class='text-body-1'
                >{mappedRoomType.value}
                </div>
              </div>
              <div class='flex space-x-2'>
                <div class='text-body-1 font-medium'>{localeTexts.value['create-booking-confirm-step3-room-start-end-label']}</div>
                <div
                  t-data='booking-confirmation-stay-date'
                  class='text-body-1'
                >{mappedStayDate.value}
                </div>
              </div>
            </div>
          </div>
          <div class='p-4'>
            <div class='text-subtitle-1 text-neutral-500'>{localeTexts.value['create-booking-confirm-step3-patient-details-title']}</div>
            <div class='mt-4 space-y-2'>
              <div class='flex space-x-2'>
                <div class='text-body-1 font-medium'>{localeTexts.value['create-booking-confirm-step3-name-label']}</div>
                <div
                  t-data='booking-confirmation-fullname'
                  class='text-body-1'
                >{firstName.value} {lastName.value}
                </div>
              </div>
              <div class='flex space-x-2'>
                <div class='text-body-1 font-medium'>{localeTexts.value['create-booking-confirm-step3-vip-label']}</div>
                <div
                  t-data='booking-confirmation-vip'
                  class='text-body-1'
                >{patientVip.value}
                </div>
              </div>
              <div class='flex space-x-2'>
                <div class='text-body-1 font-medium'>{localeTexts.value['create-booking-confirm-step3-email-label']}</div>
                <div
                  t-data='booking-confirmation-email'
                  class='text-body-1'
                >{mappedEmail.value}
                </div>
              </div>
              <div class='flex space-x-2'>
                <div class='text-body-1 font-medium'>{localeTexts.value['create-booking-confirm-step3-mobile-label']}</div>
                <div
                  t-data='booking-confirmation-mobile'
                  class='text-body-1'
                >{phone.value}
                </div>
              </div>
              <div class='flex space-x-2'>
                <div class='text-body-1 font-medium'>{localeTexts.value['create-booking-confirm-step3-insurance-label']}</div>
                <div
                  t-data='booking-confirmation-insurance'
                  class='text-body-1'
                >{mappedInsurance.value}
                </div>
              </div>
              <div class='flex space-x-2'>
                <div class='text-body-1 font-medium'>{localeTexts.value['create-booking-confirm-step3-policy-number-label']}</div>
                <div
                  t-data='booking-confirmation-policy-number'
                  class='text-body-1'
                >{mappedPolicyNumber.value}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class='fixed bottom-0 left-0 z-999 w-full border-t border-neutral-300 bg-white'>
        <div class='mx-auto flex w-full flex-col items-center gap-3 p-6 tablet:flex-row desktop:w-main-content desktop:px-0'>
          <div class='w-full flex-1 tablet:w-auto'>
            <VStepper
              t-data='booking-confirmation-stepper'
              items={bookingSteps.value}
              value={bookingCompletedStep.value}
              isFull={isMobile.value}
            />
          </div>
          <div class='flex w-full flex-none space-x-6 tablet:w-auto'>
            <VButton
              t-data='booking-confirmation-edit-booking-btn'
              class='flex-none'
              color={clsx('btn-gray')}
              onClick={() => { setBookingState('form') }}
            >
              <span class='hidden tablet:inline'>{localeTexts.value['create-booking-confirm-step3-edit-btn']}</span>
              <span class='inline tablet:hidden'>edit </span>
            </VButton>
            <VButton
              t-data='booking-confirmation-confirm-btn'
              class='flex-1 tablet:flex-initial'
              type='submit'
            >
              {localeTexts.value['create-booking-confirm-step3-confirm-btn']}
            </VButton>
          </div>
        </div>
      </div>
    </VForm>
  )
}, {
  name: 'BookingForm'
})
